/*
 * Hand-written post-processors for the few commands whose Spring Shell implementation does
 * client-side processing of the REST response (filter / pluck / sort, or use an option that is
 * never sent to the backend). The generic executor can't replicate that, so the generator flags
 * these commands `clientLogic: true` and this module supplies the equivalent transform, keyed by
 * command words.
 *
 * This file is hand-maintained and is NEVER written by tools/generate-shell-manifest.py
 * — regenerating the manifest does not touch it. A command flagged `clientLogic` without an entry
 * here is treated as unavailable (the safe baseline); the dev drift-check below warns about that.
 */
import {commands} from "./ShellManifest";
import {logger} from "components/src/utils/Logger";

type OptionValues = Record<string, string | string[]>;
export type Transform = (data: unknown, values: OptionValues) => unknown;

/** return a single field of the response object (e.g. `.value()`, `.getMyHostname()`) */
const pluck = (field: string): Transform => (d) => (d as Record<string, unknown> | null)?.[field];

const scalar = (v: string | string[] | undefined): string | undefined =>
    Array.isArray(v) ? v[0] : v;

export const POST_PROCESS: Record<string, Transform> = {
    // property
    "property available": (d, v) => {
        const cat = scalar(v.category);
        return cat
            ? (d as Array<{category?: string}>).filter(p => (p.category ?? "").toLowerCase() === cat.toLowerCase())
            : d;
    },
    "property available categories": (d) => (d as Array<{category?: string}>).map(p => p.category).sort(),
    "property user get value": pluck("value"),
    "property domain get value": pluck("value"),
    "property global get value": pluck("value"),

    // auth — Java applies `String.matches(filter)` (whole-string anchored) then sorts
    "auth permissions all": (d, v) => {
        let perms = d as string[];
        const f = scalar(v.filter);
        if (f) {
            const re = new RegExp(`^(?:${f})$`);
            perms = perms.filter(p => re.test(p));
        }
        return [...perms].sort();
    },

    // mta config main <x> get — return one field of StandardMainSettings
    "mta config main myhostname get": pluck("myHostname"),
    "mta config main mynetworks get": pluck("myNetworks"),
    "mta config main external-relay-hosts get": pluck("externalRelayHosts"),
    "mta config main internal-relay-hosts get": pluck("internalRelayHosts"),
    "mta config main relay-domains get": pluck("relayDomains"),
    "mta config main message-size-limit get": pluck("messageSizeLimit"),
    "mta config main smtp-helo-name get": pluck("smtpHeloName"),
    "mta config main reject-unverified-recipient get": pluck("rejectUnverifiedRecipient"),
    "mta config main unverified-recipient-reject-code get": pluck("unverifiedRecipientRejectCode"),
};

export function postProcessorFor(words: string[]): Transform | undefined {
    return POST_PROCESS[words.join(" ")];
}

/** clientLogic commands intentionally left unavailable (no transform). None currently. */
const INTENTIONALLY_UNAVAILABLE = new Set<string>([]);

// Drift check (dev only): every clientLogic command should have a transform or be allowlisted, so
// a newly-introduced client-side-logic command can't silently slip through as wrong output.
if (import.meta.env.DEV) {
    for (const c of commands) {
        const key = c.words.join(" ");
        if (c.clientLogic && !POST_PROCESS[key] && !INTENTIONALLY_UNAVAILABLE.has(key)) {
            logger.warn(`[shell] clientLogic command without a post-processor: ${key}`);
        }
    }
}
